import { useQuery } from '@tanstack/react-query';
import { Component, type ErrorInfo, type ReactNode, useEffect, useState } from 'react';
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { AppShell } from './components/AppShell';
import { NoAccess } from './components/NoAccess';
import { Button } from './components/ui';
import { api } from './lib/api';
import { CrumbsProvider } from './lib/crumbs';
import { ApiError, reportClientError } from './lib/errors';
import { directoryNavFor } from './lib/nav';
import { capabilities } from './lib/personnel-api';
import { APP_ROLES, type AppRole, homeOf, useRole } from './lib/role';
import { pageRoutes } from './routes.generated';

/** Граница ошибок: сбой интерфейса уходит в серверный журнал (принцип II). */
class ErrorBoundary extends Component<{ children: ReactNode }, { failed: boolean }> {
  override state = { failed: false };
  static getDerivedStateFromError() {
    return { failed: true };
  }
  override componentDidCatch(error: Error, info: ErrorInfo) {
    reportClientError(
      location.pathname,
      `render: ${info.componentStack?.split('\n')[1]?.trim() ?? ''}`,
      error,
    );
  }
  override render() {
    if (this.state.failed) {
      return (
        <div className="flex min-h-screen flex-col items-center justify-center gap-4 text-center">
          <p className="text-[15px] font-semibold">Что-то пошло не так</p>
          <p className="-mt-2 text-[13px] text-ink-2">Ошибка отправлена команде разработки.</p>
          <Button onClick={() => location.reload()}>Перезагрузить</Button>
        </div>
      );
    }
    return this.props.children;
  }
}

const roleForbidden = (e: unknown): e is ApiError => e instanceof ApiError && e.code === 'ROLE_FORBIDDEN';

const NO_ROLES: ReadonlySet<AppRole> = new Set();

function Authed() {
  const { role, objectId, setRole } = useRole();
  const location = useLocation();
  const navigate = useNavigate();
  const me = useQuery({ queryKey: ['me', role], queryFn: api.me });
  // Роли, в которых сервер ответил ROLE_FORBIDDEN на объекте `object`. Каждая пробуется один раз: без этого у
  // пользователя без назначений роли переключались по кругу, а /api/me уходил десятки раз в секунду (демо-стенд,
  // 12.10.2026). Другой объект (переключатель объекта, 007 T022) — роли пробуются заново: отказ был на прежнем.
  const [denied, setDenied] = useState<{ object: string | null; roles: ReadonlySet<AppRole> }>(() => ({
    object: objectId,
    roles: NO_ROLES,
  }));
  const deniedHere = denied.object === objectId ? denied.roles : NO_ROLES;
  const error = me.error;
  const forbidden = roleForbidden(error);
  // Следующая роль из шести, в которой сервер ещё не отказал (004, research R18): роль «Администратор правил» и
  // другие роли не из табеля тоже находятся. Не осталось — `undefined`.
  const fallback = forbidden ? APP_ROLES.find((r) => r !== role && !deniedHere.has(r)) : undefined;

  // Роли нет у пользователя — один раз перейти в следующую роль; отказ во всех — экран «Нет доступа». Открыта
  // домашняя страница отказанной роли — открыть домашнюю страницу новой; другой адрес (табель по ссылке) остаётся.
  useEffect(() => {
    if (!forbidden) return;
    setDenied((d) => {
      const here = d.object === objectId ? d.roles : NO_ROLES;
      return here.has(role) && d.object === objectId
        ? d
        : { object: objectId, roles: new Set(here).add(role) };
    });
    if (fallback === undefined) return;
    setRole(fallback);
    if (location.pathname === homeOf(role)) navigate(homeOf(fallback), { replace: true });
  }, [forbidden, role, objectId, fallback, setRole, location.pathname, navigate]);

  if (forbidden && fallback === undefined) return <NoAccess error={error} />;
  if (me.isLoading || forbidden) return <div className="p-10 text-ink-3">Загрузка…</div>;
  if (me.error || !me.data) {
    if (me.error instanceof ApiError && me.error.code === 'UNAUTHENTICATED')
      return <Navigate to="/login" replace />;
    return <div className="p-10 text-ink-2">Не удалось загрузить профиль.</div>;
  }
  // Адрес без страницы — домашняя страница роли. Её страницы может ещё не быть (`/rules` до 004 US1): тогда каркас с
  // пустым меню, а переход на тот же адрес второй раз не идёт.
  const directoryHome = directoryNavFor(capabilities(me.data))[0]?.to;
  const home = me.data.user.roles.includes(role) ? homeOf(role) : (directoryHome ?? homeOf(role));
  return (
    <AppShell me={me.data}>
      <Routes location={location}>
        {pageRoutes.map((r) =>
          r.access === 'authed' ? (
            <Route key={r.path} path={r.path} element={<r.Component me={me.data} />} />
          ) : null,
        )}
        <Route path="*" element={<Navigate to={home} replace />} />
      </Routes>
    </AppShell>
  );
}

export function App() {
  return (
    <ErrorBoundary>
      <Routes>
        {/* Страницы из web/src/pages/<путь>/route.ts — список собирает `pnpm gen` (T006). */}
        {pageRoutes.map((r) =>
          r.access === 'public' ? <Route key={r.path} path={r.path} element={<r.Component />} /> : null,
        )}
        <Route
          path="/*"
          element={
            <CrumbsProvider>
              <Authed />
            </CrumbsProvider>
          }
        />
      </Routes>
    </ErrorBoundary>
  );
}
